import * as R from 'ramda';
import { CSS } from '@dnd-kit/utilities';
import AsyncSelect from 'react-select/async';
import Select, { components } from 'react-select';
import React, { useMemo, useCallback } from 'react';
import CreatableSelect from 'react-select/creatable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  useSensor,
  DndContext,
  useSensors,
  MouseSensor,
  TouchSensor,
  closestCenter,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
// helpers/constants
import * as G from '../helpers';
import * as GC from '../constants';
// forms
import { renderBorderColor } from '../forms/formik/fieldset2/ui';
//////////////////////////////////////////////////

const dndModifiers = [restrictToVerticalAxis, restrictToParentElement];

const makeDefaultStyles = ({
  width,
  height,
  isMulti,
  hasError,
  useMaxHeight,
  valueContainerMaxHeight,
} = {}) => ({
  container: (baseStyles) => ({
    ...baseStyles,
    width,
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    marginTop: G.ifElse(hasError, 2, 8),
  }),
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 1300,
    marginTop: 0,
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: '0 2px',
  }),
  clearIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: '0 2px',
  }),
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: 4,
    boxSizing: 'border-box',
    minHeight: R.or(height, 36),
    borderColor: G.renderBorderColor({ hasError }),
    height: isMulti ? 'max-content' : R.or(height, 'max-content'),
  }),
  valueContainer: (baseStyles, props) => {
    if (R.and(useMaxHeight, G.isNotNilAndNotEmpty(R.path(['selectProps', 'value'], props)))) {
      return {
        ...baseStyles,
        padding: '2px',
        overflow: 'auto',
        maxHeight: valueContainerMaxHeight,
      };
    }

    return baseStyles;
  },
});

const ReactSelect = (props) => {
  const {
    value,
    width,
    height,
    isMulti,
    options,
    hasError,
    innerRef,
    isGrouped,
    useMaxHeight,
    additionalStyles,
    isClearable = true,
    useMenuPortalTarget,
    maxMenuHeight = 190,
    valueContainerMaxHeight,
    shouldNotGetValueFromOptions,
    noOptionsMessage = () => null,
  } = props;

  const styles = useMemo(() => ({
    ...makeDefaultStyles({ width, height, isMulti, hasError, useMaxHeight, valueContainerMaxHeight }),
    ...additionalStyles,
  }), [width, height, isMulti, hasError, useMaxHeight, additionalStyles, valueContainerMaxHeight]);

  const selectedValue = useMemo(() => {
    if (R.or(R.isNil(value), shouldNotGetValueFromOptions)) return value;

    let fullOptions = options;

    if (isGrouped) {
      fullOptions = R.compose(
        R.flatten,
        R.map(R.propOr([], 'options')),
      )(options);
    }

    if (isMulti) {
      return R.sort(
        (a, b) => R.subtract(
          R.indexOf(R.prop('value', a), value),
          R.indexOf(R.prop('value', b), value),
        ),
        R.filter((item) => R.includes(R.prop('value', item), value), R.or(fullOptions, [])),
      );
    }

    return R.find(R.propEq(value, GC.FIELD_VALUE), R.or(fullOptions, []));
  }, [value, options, isMulti, isGrouped, shouldNotGetValueFromOptions]);

  const menuProps = G.ifElse(
    useMenuPortalTarget,
    {
      menuPlacement: 'auto',
      menuPortalTarget: document.body,
      menuShouldScrollIntoView: false,
    },
  );

  const reactSelectProps = {
    ...menuProps,
    ...R.omit(
      [
        'value',
        'height',
        'hasError',
        'isGrouped',
        'useMaxHeight',
        'additionalStyles',
        'useMenuPortalTarget',
        'valueContainerMaxHeight',
        'shouldNotGetValueFromOptions',
      ],
      props,
    ),
    styles,
    isClearable,
    maxMenuHeight,
    noOptionsMessage,
    value: selectedValue,
  };

  return <Select {...reactSelectProps} ref={innerRef} />;
};

const CreatableReactSelect = (props) => {
  const {
    value,
    width,
    height,
    isMulti,
    options,
    hasError,
    innerRef,
    isGrouped,
    useMaxHeight,
    additionalStyles,
    isClearable = true,
    useMenuPortalTarget,
    maxMenuHeight = 190,
    valueContainerMaxHeight,
    shouldNotGetValueFromOptions,
    noOptionsMessage = () => null,
  } = props;

  const styles = useMemo(() => ({
    ...makeDefaultStyles({ width, height, isMulti, hasError, useMaxHeight, valueContainerMaxHeight }),
    ...additionalStyles,
  }), [width, height, isMulti, hasError, useMaxHeight, additionalStyles, valueContainerMaxHeight]);

  const selectedValue = useMemo(() => {
    if (R.or(R.isNil(value), shouldNotGetValueFromOptions)) return value;

    let fullOptions = options;

    if (isGrouped) {
      fullOptions = R.compose(
        R.flatten,
        R.map(R.propOr([], 'options')),
      )(options);
    }

    if (isMulti) {
      return R.sort(
        (a, b) => R.subtract(
          R.indexOf(R.prop('value', a), value),
          R.indexOf(R.prop('value', b), value),
        ),
        R.filter((item) => R.includes(R.prop('value', item), value), R.or(fullOptions, [])),
      );
    }

    return R.find(R.propEq(value, GC.FIELD_VALUE), R.or(fullOptions, []));
  }, [value, options, isMulti, isGrouped, shouldNotGetValueFromOptions]);

  const menuProps = G.ifElse(
    useMenuPortalTarget,
    {
      menuPlacement: 'auto',
      menuPortalTarget: document.body,
      menuShouldScrollIntoView: false,
    },
  );

  const reactSelectProps = {
    ...menuProps,
    ...R.omit(
      [
        'value',
        'height',
        'hasError',
        'isGrouped',
        'useMaxHeight',
        'additionalStyles',
        'useMenuPortalTarget',
        'valueContainerMaxHeight',
        'shouldNotGetValueFromOptions',
      ],
      props,
    ),
    styles,
    isClearable,
    maxMenuHeight,
    noOptionsMessage,
    value: selectedValue,
  };

  return <CreatableSelect {...reactSelectProps} ref={innerRef} />;
};

const SortableMultiValue = (props) => {
  const { data: { value } } = props;

  const {
    listeners,
    transform,
    attributes,
    setNodeRef,
    transition,
    isDragging,
  } = useSortable({
    id: value,
    animateLayoutChanges: () => false,
  });

  const style = {
    transition,
    maxWidth: '100%',
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      {...attributes}
      {...listeners}
      style={style}
      ref={setNodeRef}
    >
      <components.MultiValue {...props} />
    </div>
  );
};

const ReactSortableMultiselect = (props) => {
  const {
    value,
    options,
    onChange,
    hasError,
    isClearable,
    maxMenuHeight,
    additionalStyles,
    useMenuPortalTarget,
    noOptionsMessage = () => null,
  } = props;

  const styles = useMemo(
    () => ({
      ...makeDefaultStyles(),
      multiValueRemove: (baseStyles) => ({
        ...baseStyles,
        cursor: 'default',
      }),
      control: (baseStyles, { getValue }) => {
        const value = getValue();

        const common = {
          ...baseStyles,
          minHeight: 36,
          borderRadius: 4,
          height: 'max-content',
          borderColor: renderBorderColor({ hasError }),
        };

        const additional = {
          '& > div:first-of-type': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          },
        };

        return G.ifElse(G.isNilOrEmpty(value), common, { ...common, ...additional });
      },
      ...additionalStyles,
    }),
    [hasError, additionalStyles],
  );

  const selectedValue = useMemo(() => {
    if (R.isNil(value)) return value;

    return R.sort(
      (a, b) => R.subtract(
        R.indexOf(R.prop('value', a), value),
        R.indexOf(R.prop('value', b), value),
      ),
      R.filter((item) => R.includes(R.prop('value', item), value), R.or(options, [])),
    );
  }, [value, options]);

  const sortableIds = useMemo(
    () => R.map(R.prop(GC.FIELD_VALUE), R.or(selectedValue, [])),
    [selectedValue],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback(
    ({ active, over } = {}) => {
      const activeId = R.prop('id', active);
      const overId = R.prop('id', over);

      if (R.or(R.isNil(overId), R.equals(activeId, overId))) return;

      const sourceIndex = R.indexOf(activeId, sortableIds);
      const destinationIndex = R.indexOf(overId, sortableIds);

      if (R.or(R.equals(sourceIndex, -1), R.equals(destinationIndex, -1))) return;

      onChange(G.reorderList(selectedValue, sourceIndex, destinationIndex));
    },
    [onChange, selectedValue, sortableIds],
  );

  const customComponents = useMemo(() => ({
    MultiValue: (multiValueProps) => (
      <SortableMultiValue {...multiValueProps} />
    ),
  }), []);

  const menuProps = G.ifElse(
    useMenuPortalTarget,
    {
      menuPlacement: 'auto',
      menuPortalTarget: document.body,
      menuShouldScrollIntoView: false,
    },
  );

  const reactSelectProps = {
    ...menuProps,
    ...R.omit(
      [
        'value',
        'height',
        'hasError',
        'useMaxHeight',
        'additionalStyles',
        'useMenuPortalTarget',
        'valueContainerMaxHeight',
        'shouldNotGetValueFromOptions',
      ],
      props,
    ),
    styles,
    isClearable,
    maxMenuHeight,
    noOptionsMessage,
    value: selectedValue,
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={onDragEnd}
      modifiers={dndModifiers}
      collisionDetection={closestCenter}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <Select
          {...reactSelectProps}
          isMulti={true}
          styles={styles}
          options={options}
          onChange={onChange}
          closeMenuOnSelect={false}
          components={customComponents}
          noOptionsMessage={noOptionsMessage}
        />
      </SortableContext>
    </DndContext>
  );
};

const ReactAsyncSelect = (props) => {
  const { id, width, height, placeholder, getLoadOptions, handleAsyncSearchSelectChange } = props;

  const styles = useMemo(() => ({
    ...makeDefaultStyles({ width, height }),
  }), [width, height]);

  return (
    <AsyncSelect
      id={id}
      styles={styles}
      cacheOptions={true}
      maxMenuHeight={190}
      placeholder={placeholder}
      onChange={handleAsyncSearchSelectChange}
      components={{ IndicatorSeparator: () => null }}
      loadOptions={G.setDebounce(getLoadOptions, 1000)}
    />
  );
};

export {
  ReactSelect,
  ReactAsyncSelect,
  CreatableReactSelect,
  ReactSortableMultiselect,
};
